import { ChoosePeriodModal } from "@/components/filter/ChoosePeriodModal.tsx";
import {
  parseUrlHelper,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
} from "@skailer-com/uikit";
import { ApiGoalStatus } from "@/types/goals.types.ts";
import {
  addQuarters,
  addYears,
  endOfQuarter,
  endOfYear,
  getQuarter,
  getYear,
  startOfQuarter,
  startOfYear,
} from "date-fns";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

interface PeriodOption {
  id: string;
  title: string;
  start: Date | null;
  end: Date | null;
}

interface GoalStatusOption {
  id: string;
  title: string;
  statuses: ApiGoalStatus[] | null;
}

export interface GoalsCustomFiltersResult {
  status?: ApiGoalStatus[];
  date_period_start?: Date;
  date_period_end?: Date;
}

export function useGoalsCustomFilters(): {
  filterAdditions: GoalsCustomFiltersResult;
  renderFilters: () => React.ReactNode;
  renderStatusFilter: () => React.ReactNode;
} {
  const { t } = useTranslation();
  const modalsService = useModals();
  const [searchParams, setSearchParams] = useSearchParams();

  const today = new Date();
  const currentQuarter = getQuarter(today);
  const currentYear = getYear(today);
  const nextQuarterDate = addQuarters(today, 1);
  const nextQuarter = getQuarter(nextQuarterDate);
  const nextQuarterYear = getYear(nextQuarterDate);
  const nextYear = currentYear + 1;

  const periodOptions = useMemo<PeriodOption[]>(
    () => [
      { id: "all_time", title: t("common:all_time"), start: null, end: null },
      {
        id: "current_quarter",
        title: t("goals:quarter_format", { quarter: currentQuarter, year: currentYear }),
        start: startOfQuarter(today),
        end: endOfQuarter(today),
      },
      {
        id: "next_quarter",
        title: t("goals:quarter_format", { quarter: nextQuarter, year: nextQuarterYear }),
        start: startOfQuarter(nextQuarterDate),
        end: endOfQuarter(nextQuarterDate),
      },
      {
        id: "current_year",
        title: String(currentYear),
        start: startOfYear(today),
        end: endOfYear(today),
      },
      {
        id: "next_year",
        title: String(nextYear),
        start: startOfYear(addYears(today, 1)),
        end: endOfYear(addYears(today, 1)),
      },
      { id: "custom", title: t("common:custom_period"), start: null, end: null },
    ],
    [t, currentQuarter, currentYear, nextQuarter, nextQuarterYear, nextYear]
  );

  const goalStatusOptions = useMemo<GoalStatusOption[]>(
    () => [
      { id: "open", title: t("goals:open_goals"), statuses: [ApiGoalStatus.active] },
      {
        id: "closed",
        title: t("goals:closed_goals"),
        statuses: [ApiGoalStatus.completed, ApiGoalStatus.failed, ApiGoalStatus.cancelled],
      },
      { id: "all", title: t("goals:all_goals"), statuses: null },
    ],
    [t]
  );

  const periodParam = searchParams.get("period");
  const datePeriodStart = searchParams.get("date_period_start");
  const datePeriodEnd = searchParams.get("date_period_end");
  const goalStatusParam = searchParams.get("goal_status") || "open";

  const selectedPeriod = useMemo(() => {
    if (datePeriodStart && datePeriodEnd && !periodParam) {
      return {
        id: "custom",
        title: t("common:custom_period"),
        start: new Date(datePeriodStart),
        end: new Date(datePeriodEnd),
      };
    }
    return periodOptions.find((opt) => opt.id === (periodParam || "all_time")) || periodOptions[0];
  }, [periodParam, datePeriodStart, datePeriodEnd, periodOptions, t]);

  const selectedGoalStatus = useMemo(() => {
    return goalStatusOptions.find((opt) => opt.id === goalStatusParam) || goalStatusOptions[0];
  }, [goalStatusParam, goalStatusOptions]);

  const filterAdditions = useMemo<GoalsCustomFiltersResult>(() => {
    const result: GoalsCustomFiltersResult = {};
    if (selectedGoalStatus.statuses) {
      result.status = selectedGoalStatus.statuses;
    }
    if (selectedPeriod.start && selectedPeriod.end) {
      result.date_period_start = selectedPeriod.start;
      result.date_period_end = selectedPeriod.end;
    }
    return result;
  }, [selectedGoalStatus, selectedPeriod]);

  const handlePeriodChange = (option: PeriodOption) => {
    if (option.id === "custom") {
      modalsService.openModal(ChoosePeriodModal, {
        dateFrom: datePeriodStart ? new Date(datePeriodStart) : undefined,
        dateTo: datePeriodEnd ? new Date(datePeriodEnd) : undefined,
        onApply: (dfrom: Date, dto: Date) => {
          setSearchParams((params) => {
            params.set("date_period_start", parseUrlHelper.formatDate(dfrom));
            params.set("date_period_end", parseUrlHelper.formatDate(dto));
            params.delete("period");
            params.set("page", "1");
            return params;
          });
        },
      });
    } else {
      setSearchParams((params) => {
        params.delete("date_period_start");
        params.delete("date_period_end");
        if (option.id === "all_time") {
          params.delete("period");
        } else {
          params.set("period", option.id);
        }
        params.set("page", "1");
        return params;
      });
    }
  };

  const handleGoalStatusChange = (option: GoalStatusOption) => {
    setSearchParams((params) => {
      params.set("goal_status", option.id);
      params.set("page", "1");
      return params;
    });
  };

  const renderFilters = () => (
    <>
      <DropdownMenu
        trigger={
          <Button
            variant={"secondary"}
            rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
          >
            {selectedPeriod.title}
          </Button>
        }
      >
        {periodOptions.map((option) => (
          <DropdownMenuItem
            key={`period-${option.id}`}
            title={option.title}
            isSelected={selectedPeriod.id === option.id}
            onClick={() => handlePeriodChange(option)}
          />
        ))}
      </DropdownMenu>
      <DropdownMenu
        trigger={
          <Button
            variant={"secondary"}
            rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
          >
            {selectedGoalStatus.title}
          </Button>
        }
      >
        {goalStatusOptions.map((option) => (
          <DropdownMenuItem
            key={`goal-status-${option.id}`}
            title={option.title}
            isSelected={selectedGoalStatus.id === option.id}
            onClick={() => handleGoalStatusChange(option)}
          />
        ))}
      </DropdownMenu>
    </>
  );

  const renderStatusFilter = () => (
    <DropdownMenu
      trigger={
        <Button
          variant={"secondary"}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-bars-filter"} />}
          rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
        >
          {selectedGoalStatus.title}
        </Button>
      }
    >
      {goalStatusOptions.map((option) => (
        <DropdownMenuItem
          key={`goal-status-${option.id}`}
          title={option.title}
          isSelected={selectedGoalStatus.id === option.id}
          onClick={() => handleGoalStatusChange(option)}
        />
      ))}
    </DropdownMenu>
  );

  return { filterAdditions, renderFilters, renderStatusFilter };
}
